/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { makeStyles, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ListSubheader from '@material-ui/core/ListSubheader';
import Divider from '@material-ui/core/Divider';
import Menu from '@material-ui/core/Menu';
import FilesSelectorOption from '../../common/FilesSelectorOption';
import FilesSelectorSelect from '../../common/FilesSelectorSelect';

const useStyles = makeStyles({
  epigraph: {
    marginBottom: '.5rem',
  },
  menu: {
    padding: 0,
  },
  attachmentSection: {
    fontSize: '.875rem',
  },
});

/**
 * Preview select for submission components for screens smaller than extra large.
 * It contains 2 sections:
 * - unclassified top entries components
 * - attachment files grouped by categories
 */
export default function PreviewFilesSelect({ className, topEntries, attachments, previewObject, loadAttachmentFile }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [anchorMenu, setAnchorMenu] = useState(null);

  const openMenu = e => setAnchorMenu(e.currentTarget);
  const closeMenu = () => setAnchorMenu(null);
  const isMenuOpen = () => !!anchorMenu;
  const handleMenuItemClick = (fn) => {
    fn();
    closeMenu();
  };

  // find the active select entry
  let title;
  let subtitle;
  let icon;

  const selectedEntry = topEntries?.find(entry => entry.id === previewObject?.id);
  if (selectedEntry) {
    title = selectedEntry.title;
    subtitle = selectedEntry.subtitle;
    icon = selectedEntry?.icon;
  } else {
    const fileEntry = attachments
      ?.map(attachment => attachment.files)
      .flat()
      .find(f => f.id === previewObject?.id);

    title = fileEntry?.attachmentType;
    subtitle = fileEntry?.originalFileName;
  }

  return (
    <div className={className}>
      <Typography variant="body1" className={classes.epigraph}>
        <strong>{t('file.select.file.to.preview')}</strong>
      </Typography>
      <FilesSelectorSelect
        title={title}
        subtitle={subtitle}
        isActive={true}
        isOpen={isMenuOpen()}
        onClick={openMenu}
        ariaControls="menu-files"
        icon={icon}
      />
      <Menu
        id="menu-files"
        anchorEl={anchorMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        getContentAnchorEl={null}
        open={isMenuOpen()}
        onClose={closeMenu}
        MenuListProps={{ className: classes.menu }}
      >
        {/* non attachments entries on top of the select */}
        {topEntries.map(entry => (
          <FilesSelectorOption
            title={entry.title}
            subtitle={entry.subtitle}
            isActive={previewObject?.id === entry.id}
            onClick={() => handleMenuItemClick(entry.handleEntryClick)}
          />
        ))}

        {/* attachment files grouped by category */}
        {attachments.map(attachment => (
          <>
            <ListSubheader>
              <Divider />
              <Typography variant="body1" className={classes.attachmentSection}>
                {attachment.category}
              </Typography>
            </ListSubheader>

            {attachment.files?.map((file) => {
              return (
                <FilesSelectorOption
                  title={t(file.attachmentType)}
                  subtitle={file.originalFileName}
                  isActive={previewObject?.id === attachment.id}
                  onClick={() => handleMenuItemClick(() => loadAttachmentFile(file))}
                />
              );
            })}
          </>
        ))}
      </Menu>
    </div>
  );
}
